import React from 'react';
// A "photo" glyph, not an expand/fullscreen one (Alexey 2026-09-06: "можно
// везде иконку не расширения в стороны, а фотографии") — every chart-open
// affordance in this file opens a photo/chart view, so the icon should read
// as "open the picture", not "resize".
import { Image as PhotoIcon } from 'lucide-react';

import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Renders the daily-report `body_html` (the same curated text that goes to
 * Telegram) compactly for the web:
 *   - strips the TG "cap" (🏁 Итоги дня / Филиал / Дата / Полный отчет /
 *     Дашборды — see CAP_PREFIXES), which the web shows in the header + a
 *     bottom Grafana link row instead. «Полный отчет: Смотреть» in
 *     particular is a self-link back to THIS page (composer.py bakes it into
 *     every body_html, TG copy included — 2026-09-03 tree-format rewrite) —
 *     it's correct in Telegram but pointless here, so it's stripped at
 *     RENDER time only; the stored body_html (and the TG message) are
 *     untouched (Alexey 2026-09-04);
 *   - renders each bare `<b>Section</b>` top-level header (composer.py's
 *     ReportSection.title — "Выкладка" / "Очередь и бар") as its own small
 *     card via SectionCard, instead of a plain inline bold line;
 *   - renders «├/└»-connected tree branches (empty_shelf's per-category
 *     lines, checkout_control's «Самая длинная очередь») via TreeBranches —
 *     a real CSS connector rail instead of raw unicode text, which reads as
 *     choppy in a proportional font — and turns any inline `<a href>` on a
 *     branch into a small «Посмотреть» button instead of a raw link;
 *   - renders the OLD «━━━━━ Title ━━━━━» / «━━━ Section: Sub ━━━» dividers
 *     the same way as before (back-compat for reports stored before the
 *     2026-09-03 tree-format rewrite — those never carry `<b>` section
 *     headers or ├/└ branches at all);
 *   - collapses ANY run of blank lines into one small uniform gap (validators
 *     remove the extra \n inconsistently, so we normalise here);
 *   - drops the extra blank after a <blockquote>;
 *   - removes empty `<a></a>` anchors (the backend sometimes emits a duplicate
 *     empty link that would otherwise show a stray ↗) and opens the real links
 *     in a new tab with a Grafana hint (the ↗ comes from globals.css).
 */

const CAP_PREFIXES = [
  '🏁',
  'Филиал:',
  'Дата:',
  // Old label (pre 2026-09-03 tree-format rewrite: "Период:" → "Дата:") —
  // kept for back-compat with reports stored before the rename.
  'Период:',
  'Полный отчет:',
  'Дашборды:',
  'Здесь могла бы быть',
];
// Fallback ONLY — old reports (generated before Round-2, 2026-09) never
// have a real "Выкладка: Ключевые точки" divider in their body_html at
// all, just the bare 🥖/🍱 lines. Back-compat (Alexey 2026-09-02: "должна
// быть back compatibility для старых отчетов") means still inserting a
// divider for those; new reports skip this because a real one already
// rendered (see keyPointsSeen below).
const KEY_POINT_RE = /^(🥖|🍱|🍰|🧁)/u;

// Which report chart a "Посмотреть" button opens, and the X window to zoom to.
export type ReportChartTarget = 'racks' | 'queue';
export type OpenReportChart = (
  target: ReportChartTarget,
  domain: [number, number],
  rackName?: string,
  /** The UNPADDED problem window from the link — the chart shades it as a
   *  band, so the problem stays visible even after a zoom-out to the whole
   *  day (Alexey 2026-09-07: "какую-то подсветку сегмента сделать хотя бы"). */
  window?: [number, number],
  /** `?rack_id=` off the BE deep link (2026-09-08) — exact rack, no name match. */
  rackId?: number,
) => void;

const PROBLEM_ZONES_PREFIX = 'Ссылки на проблемные зоны';
const LONGEST_QUEUE_MARKER = 'Самая длинная';
// A bare `<b>Title</b>` line — composer.py's per-section header since the
// 2026-09-03 tree-format rewrite (was "━━━━━ Title ━━━━━"). Matched against
// the RAW line (trimmed), not stripped text, so it never fires on inline
// `<b>` usage inside a sentence (e.g. table_control's "<b>3</b> раза").
const SECTION_HEADER_RE = /^<b>([^<]+)<\/b>$/i;
// A ├/└-connected tree branch (composer.py's render_tree_branches).
const TREE_BRANCH_RE = /^[├└]/;
// A line that's ENTIRELY one `<a>` and nothing else — checklist.py's bare
// "настроить чеклист" line is the only section that emits a link this way
// (every other link rides inside a sentence/branch and is handled above);
// render it as a real button instead of a plain inline text link.
const SOLE_ANCHOR_RE = /^<a\b[^>]*\bhref=['"]([^'"]+)['"][^>]*>([\s\S]*?)<\/a>$/i;

function stripTags(s: string): string {
  return s.replace(/<[^>]+>/g, '').trim();
}

/** Pull `[href, innerText]` out of every `<a>` in a fragment (both quote styles). */
function extractAnchors(html: string): { href: string; label: string }[] {
  const re = /<a\b[^>]*\bhref=['"]([^'"]+)['"][^>]*>([\s\S]*?)<\/a>/gi;
  const out: { href: string; label: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    out.push({ href: m[1], label: stripTags(m[2]) });
  }
  return out;
}

/** Grafana links carry the problem window as `?from=<ms>&to=<ms>`. Parse it into
 *  a domain padded by 10% of the span on each side (so the zoomed chart shows a
 *  bit of lead-in / tail around the problem). Returns null if unparseable. */
function periodFromHref(href: string): [number, number] | null {
  const qi = href.indexOf('?');
  if (qi < 0) return null;
  const params = new URLSearchParams(href.slice(qi + 1).replace(/&amp;/g, '&'));
  const from = Number(params.get('from'));
  const to = Number(params.get('to'));
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  const pad = (to - from) * 0.1;
  return [from - pad, to + pad];
}

/** The raw `?from=&to=` window off the href, unpadded (see periodFromHref for
 *  the padded zoom domain). */
function windowFromHref(href: string): [number, number] | undefined {
  const qi = href.indexOf('?');
  if (qi < 0) return undefined;
  const params = new URLSearchParams(href.slice(qi + 1).replace(/&amp;/g, '&'));
  const from = Number(params.get('from'));
  const to = Number(params.get('to'));
  return Number.isFinite(from) && Number.isFinite(to) && to > from ? [from, to] : undefined;
}

/** `?rack=<name>` off a category-header href (build_problem_zone_link sets it
 *  since 2026-09-07) — the in-page icon click has to route to that rack just
 *  like the URL-param deep-link path in DailyReportView does. */
function rackFromHref(href: string): string | undefined {
  const qi = href.indexOf('?');
  if (qi < 0) return undefined;
  const params = new URLSearchParams(href.slice(qi + 1).replace(/&amp;/g, '&'));
  return params.get('rack') ?? undefined;
}

/** `?rack_id=<id>` — build_problem_zone_link adds it since 2026-09-08 so the
 *  page opens exactly that rack (name matching was fragile). */
function rackIdFromHref(href: string): number | undefined {
  const qi = href.indexOf('?');
  if (qi < 0) return undefined;
  const params = new URLSearchParams(href.slice(qi + 1).replace(/&amp;/g, '&'));
  const id = Number(params.get('rack_id'));
  return Number.isInteger(id) && id > 0 ? id : undefined;
}

/** Same-shaped links carry `?target=racks|queue` (both build_problem_zone_link
 *  and get_checkout_problem_zone_link set it — item 9, Alexey 2026-09). Reading
 *  it off the href (rather than hardcoding per call site) means a tree branch's
 *  link opens whichever chart it actually points at. */
function targetFromHref(href: string): ReportChartTarget | null {
  const qi = href.indexOf('?');
  if (qi < 0) return null;
  const params = new URLSearchParams(href.slice(qi + 1).replace(/&amp;/g, '&'));
  const t = params.get('target');
  return t === 'racks' || t === 'queue' ? t : null;
}

function isCapLine(line: string): boolean {
  const t = stripTags(line);
  return CAP_PREFIXES.some((p) => t.startsWith(p));
}

function cleanLinks(html: string): string {
  return html
    // drop empty anchors (duplicate links with no visible text)
    .replace(/<a[^>]*>\s*<\/a>/gi, '')
    // open real links in a new tab with a Grafana hint
    .replace(/<a\s+/gi, '<a target="_blank" rel="noreferrer" title="Открыть в Grafana" ');
}

/** The pretty "Посмотреть" pill used for problem-zone / queue / tree-branch rows. */
function ViewButton({
  label,
  onClick,
  className,
}: {
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      className={cn(
        'border-primary/30 text-primary hover:border-primary/50 hover:bg-primary/10 hover:text-primary',
        className,
      )}
    >
      <PhotoIcon className="size-3.5" />
      {label}
    </Button>
  );
}

/** Icon-only chart-open affordance for a HEADER line that already names its
 *  own section ("🟡 Выпечка", "🟡 Самая длинная очередь") — a text label
 *  would just repeat the header (Alexey 2026-09-06). */
function IconViewButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="outline"
      size="icon-sm"
      onClick={onClick}
      aria-label="Посмотреть"
      className="size-6 shrink-0 border-primary/30 text-primary hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
    >
      <PhotoIcon className="size-3" />
    </Button>
  );
}

/** Renders the «Ссылки на проблемные зоны …» blockquote as a bare row of
 *  per-rack buttons directly under the category line — no box, no label, a
 *  TIGHT gap above (the buttons belong to that line) and the normal
 *  inter-line gap after. */
function ProblemZonesButtons({
  anchors,
  onOpenChart,
}: {
  anchors: { href: string; label: string }[];
  onOpenChart: OpenReportChart;
}) {
  return (
    <div className="mt-0.5 flex flex-wrap gap-1.5">
      {anchors.map((a, i) => {
        const domain = periodFromHref(a.href);
        if (!domain) {
          return (
            <span
              key={i}
              className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground"
            >
              {a.label}
            </span>
          );
        }
        return (
          <ViewButton
            key={i}
            // Icon-only "open" affordance + the rack name; the maximize glyph
            // makes the action obvious, so the «Посмотреть» prefix is dropped.
            label={a.label}
            // K4: стеллаж — из ссылки (rack_id, затем rack=), подпись — запасной вариант;
            // без этого кнопка открывала стеллаж, выбранный в панели до клика.
            onClick={() => onOpenChart('racks', domain, rackFromHref(a.href) ?? a.label, windowFromHref(a.href), rackIdFromHref(a.href))}
          />
        );
      })}
    </div>
  );
}

function SubDivider({ label }: { label: string }) {
  return (
    <div className="mt-3 mb-1 flex items-center gap-2">
      <span className="h-px w-4 bg-border" />
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/80">
        {label}
      </span>
      <span className="h-px flex-1 bg-border/60" />
    </div>
  );
}

/** One «├/└»-connected tree block (empty_shelf's per-category «Просадки / На
 *  открытии / …» branches, checkout_control's «Самая длинная очередь» —
 *  Round-2, 2026-09 tree-format rewrite). Raw ├/└ characters in a
 *  proportional font don't line up between rows of different lengths and read
 *  as choppy (Alexey 2026-09-04: "ветки рендерить покрасивее мб, сейчас они
 *  разрывистые") — this draws a real CSS connector instead: a vertical rail
 *  down the block with a horizontal tick per row, the last row's rail
 *  stopping halfway (the └ corner). Any inline `<a href>` on a branch (the
 *  queue peak/drain drill-down link) is stripped from the text and
 *  re-appended as a small «Посмотреть» button — no raw link inside a branch
 *  row (Alexey: "без ссылок а с концевыми маленькими кнопками"). */
function TreeBranches({
  lines,
  gapCls,
  onOpenChart,
}: {
  /** Raw branch lines, ├/└-prefixed, in order — the LAST one is always the
   *  └ line (guaranteed by the backend's render_tree_branches). */
  lines: string[];
  gapCls: string;
  onOpenChart?: OpenReportChart;
}) {
  return (
    <div className={cn(gapCls, 'mb-0.5')}>
      {lines.map((raw, i) => {
        const isLast = i === lines.length - 1;
        const inner = raw.trim().replace(/^[├└]\s?/, '');
        const anchor = extractAnchors(inner)[0];
        const domain = anchor ? periodFromHref(anchor.href) : null;
        const target = anchor ? targetFromHref(anchor.href) ?? 'queue' : null;
        const plain = anchor ? inner.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1') : inner;
        return (
          <div key={i} className="relative flex flex-wrap items-baseline gap-x-1.5 py-0.5 pl-5">
            <span
              aria-hidden
              className={cn('absolute left-1.5 top-0 w-px bg-border', isLast ? 'h-1/2' : 'h-full')}
            />
            <span aria-hidden className="absolute left-1.5 top-1/2 h-px w-3 bg-border" />
            <span dangerouslySetInnerHTML={{ __html: cleanLinks(plain) }} />
            {domain && target && onOpenChart && (
              <ViewButton
                label="Посмотреть"
                onClick={() => onOpenChart(target, domain, rackFromHref(anchor.href), windowFromHref(anchor.href), rackIdFromHref(anchor.href))}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** «Выкладка» / «Очередь и бар» top-level section. Since the 2026-09-03
 *  tree-format rewrite composer.py emits these as a bare `<b>Title</b>` line
 *  (the old «━━━━━ Title ━━━━━» divider is gone — see empty_shelf.py's
 *  build_empty_shelf_report_section); rendered as plain bold text that read
 *  as flat/undifferentiated (Alexey 2026-09-04: "заголовки разделов
 *  рендерить покрасивее в карточке") — wrap the section's own content in a
 *  light nested card instead. */
function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4 first:mt-0">
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
        <span className="h-px flex-1 bg-border/60" />
      </div>
      {children}
    </div>
  );
}

/** Split the (already cap-stripped) lines into top-level sections on bare
 *  `<b>Title</b>` header lines. Old reports (no such header at all — they
 *  used the ━━━━━ divider scheme instead) come back as ONE untitled segment,
 *  so they render exactly as before (ungrouped, dividers handled inline by
 *  renderSegmentLines). */
function splitSections(lines: string[]): { title: string | null; lines: string[] }[] {
  const segments: { title: string | null; lines: string[] }[] = [];
  let current: { title: string | null; lines: string[] } | null = null;
  const preamble: string[] = [];
  for (const line of lines) {
    const m = line.trim().match(SECTION_HEADER_RE);
    if (m) {
      if (current) segments.push(current);
      current = { title: m[1], lines: [] };
    } else if (current) {
      current.lines.push(line);
    } else {
      preamble.push(line);
    }
  }
  if (current) segments.push(current);
  const hasPreambleContent = preamble.some((l) => stripTags(l) !== '' || l.includes('<'));
  return hasPreambleContent ? [{ title: null, lines: preamble }, ...segments] : segments;
}

/** Render one segment's lines (everything between two section headers, or the
 *  whole body for old reports without any). Owns its own blank-run / quote /
 *  tree-branch-buffer state, reset per segment so the first line inside a
 *  card doesn't inherit spacing decisions from the previous section. */
function renderSegmentLines(
  lines: string[],
  keyPrefix: string,
  onOpenChart?: OpenReportChart,
): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let blankRun = 0;
  let lastWasQuote = false;
  let firstContent = true;
  let keyPointsSeen = false;
  // The last PLAIN line rendered — a single problem-zone link merges into it
  // as an inline button (same pattern as «Самая длинная очередь»).
  let lastPlain: { html: string; cls: string; key: string } | null = null;
  // Buffered ├/└ branch lines, flushed as one TreeBranches block on the └ line
  // (render_tree_branches always ends a group with it, so no lookahead needed).
  let branchBuffer: string[] = [];
  let branchGapCls = 'mt-0.5';

  const flushBranches = (key: string) => {
    if (!branchBuffer.length) return;
    out.push(
      <TreeBranches key={key} lines={branchBuffer} gapCls={branchGapCls} onOpenChart={onOpenChart} />,
    );
    branchBuffer = [];
    lastWasQuote = false;
    lastPlain = null;
  };

  lines.forEach((line, i) => {
    const key = `${keyPrefix}-${i}`;
    const text = stripTags(line);
    if (text === '' && !line.includes('<')) {
      blankRun += 1;
      return;
    }

    const isBranch = TREE_BRANCH_RE.test(line.trim());
    const isDivider = !isBranch && line.includes('━');
    const isQuote = !isBranch && line.trim().startsWith('<blockquote');

    // Uniform small gap before any block (one gap regardless of how many blank
    // lines preceded it); none after a quote / at the very top.
    const gapCls = firstContent || lastWasQuote ? 'mt-1.5' : blankRun > 0 ? 'mt-1.5' : 'mt-0.5';

    if (isBranch) {
      if (!branchBuffer.length) branchGapCls = gapCls;
      branchBuffer.push(line);
      blankRun = 0;
      firstContent = false;
      if (line.trim().startsWith('└')) flushBranches(key);
      return;
    }
    // A non-branch line ends whatever branch group was open (defensive —
    // normally the └ line above already flushed it).
    flushBranches(`${key}-flush`);

    // Fallback for old reports: no real subsection divider ever showed up
    // before the first 🥖/🍱 line, so fabricate one (new reports already
    // set keyPointsSeen from the real divider above and skip this).
    if (!isDivider && !keyPointsSeen && KEY_POINT_RE.test(text)) {
      out.push(<SubDivider key={`kp-${key}`} label="Выкладка: Ключевые точки" />);
      keyPointsSeen = true;
    }

    if (isDivider) {
      const label = stripTags(line.replace(/━/g, ''));
      // The backend emits two dash-weights (Round-2, 2026-09): 5-dash
      // top-level «Выкладка» / «Очередь и бар» section titles, and 3-dash
      // «Выкладка: Просадки» / «Выкладка: Ключевые точки» subsection
      // titles nested inside «Выкладка». A colon in the label is exactly
      // that "Section: Subsection" shape — render it a size down (the
      // same SubDivider this file used to fabricate itself via a 🥖/🍱
      // heuristic before the backend had real headers to say it with).
      if (label.includes(':')) {
        if (label.includes('Ключевые точки')) keyPointsSeen = true;
        out.push(<SubDivider key={key} label={label} />);
        lastWasQuote = false;
        lastPlain = null;
        blankRun = 0;
        firstContent = false;
        return;
      }
      out.push(
        <div key={key} className={`mb-1 flex items-center gap-3 ${firstContent ? 'mt-1' : 'mt-4'}`}>
          <span className="h-px flex-1 bg-border" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>,
      );
      lastWasQuote = false;
      lastPlain = null;
    } else if (isQuote) {
      const inner = line.trim().replace(/^<blockquote>/i, '').replace(/<\/blockquote>$/i, '');
      // «Ссылки на проблемные зоны: <rack>, …» → pretty per-rack buttons that
      // open the Выкладка chart zoomed to the problem window (this view only).
      // A SINGLE problem zone renders as one inline «<rack>» button — no
      // labelled group box for one item (mirrors the Самая длинная очередь row).
      const zoneAnchors = extractAnchors(inner);
      const singleDomain = zoneAnchors.length === 1 ? periodFromHref(zoneAnchors[0].href) : null;
      if (onOpenChart && text.startsWith(PROBLEM_ZONES_PREFIX) && singleDomain && lastPlain) {
        // ONE problem zone → the button joins the category line itself,
        // exactly like the «Самая длинная очередь» row. NOT a flex row: a
        // flex container places the button AFTER the text's own box, so a
        // long verdict that already wraps to 2 lines leaves the button with
        // no room and pushes it to a row of its own. Plain inline flow lets
        // the (inline-flex, so still an atomic box) button ride the text's
        // own last line, and only drop to the next line when it truly
        // doesn't fit — that's the "inline unless it has to wrap" behaviour.
        const prev = lastPlain;
        out[out.length - 1] = (
          <div key={prev.key} className={prev.cls}>
            <span dangerouslySetInnerHTML={{ __html: prev.html }} />{' '}
            <ViewButton
              label={zoneAnchors[0].label}
              onClick={() => onOpenChart('racks', singleDomain, rackFromHref(zoneAnchors[0].href) ?? zoneAnchors[0].label, windowFromHref(zoneAnchors[0].href), rackIdFromHref(zoneAnchors[0].href))}
              className="align-middle"
            />
          </div>
        );
      } else if (onOpenChart && text.startsWith(PROBLEM_ZONES_PREFIX) && zoneAnchors.length > 0) {
        out.push(<ProblemZonesButtons key={key} anchors={zoneAnchors} onOpenChart={onOpenChart} />);
      } else {
        out.push(
          <blockquote key={key} className="mt-2" dangerouslySetInnerHTML={{ __html: cleanLinks(inner) }} />,
        );
      }
      lastWasQuote = true;
      lastPlain = null;
    } else {
      // Header line carrying its own single deep-link — either the
      // empty_shelf category header ("🟡 Выпечка", build_problem_zone_link,
      // target=racks) or the queue header ("🟡 Самая длинная очередь",
      // get_checkout_problem_zone_link, target=queue). Both moved off their
      // old per-branch/per-sentence link placement onto the header itself
      // (Alexey 2026-09-06) — icon-only, since the header text already
      // names the section. `text.includes(LONGEST_QUEUE_MARKER)` is a
      // fallback target for legacy flat-sentence reports predating the
      // `target=` query param.
      const soleAnchor = extractAnchors(line)[0];
      const soleDomain = soleAnchor ? periodFromHref(soleAnchor.href) : null;
      const soleTarget = soleAnchor
        ? targetFromHref(soleAnchor.href) ?? (text.includes(LONGEST_QUEUE_MARKER) ? 'queue' : null)
        : null;
      if (onOpenChart && soleAnchor && soleDomain && soleTarget) {
        const plain = line.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1');
        out.push(
          <div key={key} className={cn(gapCls, 'flex flex-wrap items-center gap-2')}>
            <span dangerouslySetInnerHTML={{ __html: cleanLinks(plain) }} />
            <IconViewButton
              onClick={() =>
                onOpenChart(
                  soleTarget,
                  soleDomain,
                  rackFromHref(soleAnchor.href),
                  windowFromHref(soleAnchor.href),
                  rackIdFromHref(soleAnchor.href),
                )
              }
            />
          </div>,
        );
        lastPlain = null;
      } else if (SOLE_ANCHOR_RE.test(line.trim())) {
        const [, href, label] = line.trim().match(SOLE_ANCHOR_RE)!;
        out.push(
          <div key={key} className={cn(gapCls, 'mt-2')}>
            <a
              href={href.replace(/&amp;/g, '&')}
              target="_blank"
              rel="noreferrer"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'report-body-btn')}
            >
              {stripTags(label)}
            </a>
          </div>,
        );
        lastPlain = null;
      } else {
        out.push(<div key={key} className={gapCls} dangerouslySetInnerHTML={{ __html: cleanLinks(line) }} />);
        lastPlain = { html: cleanLinks(line), cls: gapCls, key };
      }
      lastWasQuote = false;
    }

    blankRun = 0;
    firstContent = false;
  });

  flushBranches(`${keyPrefix}-tail`);

  return out;
}

export function ReportBody({
  html,
  onOpenChart,
}: {
  html: string;
  /** When provided (only the /usage/report view passes it — report-validation
   *  renders body_html through a different editor), the «проблемные зоны» links
   *  and tree-branch / «Самая длинная очередь» links render as chart-opening
   *  BUTTONS rather than inline text links. Absent → the classic inline-link
   *  rendering. */
  onOpenChart?: OpenReportChart;
}) {
  let lines = html.split('\n').filter((l) => !isCapLine(l));
  while (lines.length && stripTags(lines[0]) === '' && !lines[0].includes('<')) {
    lines = lines.slice(1);
  }

  const segments = splitSections(lines);
  const out: React.ReactNode[] = [];
  segments.forEach((segment, si) => {
    const rendered = renderSegmentLines(segment.lines, `seg${si}`, onOpenChart);
    if (segment.title) {
      out.push(
        <SectionCard key={`section-${si}`} title={segment.title}>
          {rendered}
        </SectionCard>,
      );
    } else {
      out.push(...rendered);
    }
  });

  return <div className="report-body text-sm leading-relaxed text-foreground">{out}</div>;
}
